// src/app/services/performance-series/registry.ts
//
// Data-driven presentation rules for the series layers. There is no hardcoded
// list of metrics: the lenses are DERIVED from whatever layers the envelope
// carries, and each unit/group/dimension/member is given a label and ordering by
// lookup with a safe default. A brand-new backend layer (or unit, or dimension)
// therefore shows up as a fully-formed lens with zero edits here.
//
// Imports the central model catalog so model members reuse canonical labels and
// the analytics-exclusion rule (gptweb). Everything else is pure.

import {
  getModelLabel,
  isExcludedFromAnalytics,
  normalizeProviderKey,
} from "@/app/constants/models";
import type {
  NormalizedEnvelope,
  NormalizedLayer,
  Unit,
  LayerGroup,
  DimensionName,
} from "./types";

// ── Units ────────────────────────────────────────────────────────────────────
// `chartScale` lifts a native value into the 0–100 display space the shared chart
// shell expects; `suffix` is the unit shown after the number.

interface UnitFormat {
  chartScale: number;
  suffix: string;
  decimals: number;
}

const UNIT_FORMAT: Record<string, UnitFormat> = {
  score: { chartScale: 1, suffix: "", decimals: 1 },
  index: { chartScale: 1, suffix: "", decimals: 1 },
  rate: { chartScale: 100, suffix: "%", decimals: 1 },
  count: { chartScale: 1, suffix: "", decimals: 0 },
};

const DEFAULT_UNIT_FORMAT: UnitFormat = { chartScale: 1, suffix: "", decimals: 1 };

export function unitFormat(unit: Unit): UnitFormat {
  return UNIT_FORMAT[unit] ?? DEFAULT_UNIT_FORMAT;
}

/** Display scale factor (multiply native value to reach 0–100 display space). */
export function chartScaleFor(unit: Unit): number {
  return unitFormat(unit).chartScale;
}

/** Unit suffix for the chart ("%", "", ...). */
export function chartSuffixFor(unit: Unit): string {
  return unitFormat(unit).suffix;
}

/** Format a NATIVE value for text display (KPIs, tooltips outside the chart). */
export function formatValue(unit: Unit, nativeValue: number): string {
  const f = unitFormat(unit);
  if (!Number.isFinite(nativeValue)) return "n/a";
  return `${(nativeValue * f.chartScale).toFixed(f.decimals)}${f.suffix}`;
}

// ── Groups ───────────────────────────────────────────────────────────────────

interface GroupMeta {
  label: string;
  order: number;
}

// The funnel leads Performance. The "headline" group held only the AI Index,
// which is off Performance entirely (UPM-139): the measured view has no
// self-reported snapshot index. Within the funnel group the layer order comes
// from the backend LAYER_PROVIDERS list, which is primary recommendation,
// top 5, mention rate. An unknown group still falls back to order 99, so
// restoring a layer here is additive.
// Exported for the pinned product rulings, which assert on this structure
// directly so a reverted edit cannot pass unnoticed.
export const GROUP_META: Record<string, GroupMeta> = {
  funnel: { label: "Funnel", order: 0 },
  variables: { label: "Research Variables", order: 1 },
  personas: { label: "Personas", order: 2 },
  competitive: { label: "Competitive", order: 3 },
};

export function groupLabel(group: LayerGroup): string {
  return GROUP_META[group]?.label ?? humanize(group);
}

export function groupOrder(group: LayerGroup): number {
  return GROUP_META[group]?.order ?? 99;
}

// ── Dimensions ───────────────────────────────────────────────────────────────

const DIMENSION_LABEL: Record<string, string> = {
  overall: "Overall",
  model: "By model",
  persona: "By persona",
  variable: "By variable",
  competitor: "By competitor",
};

export function dimensionLabel(dimension: DimensionName): string {
  return DIMENSION_LABEL[dimension] ?? `By ${humanize(dimension).toLowerCase()}`;
}

/**
 * The breakdown a lens shows by default when rendered on its own: per-model
 * where available (matching the current per-model charts), otherwise the
 * aggregate, otherwise the layer's only breakdown.
 */
export function defaultDimension(dimensions: DimensionName[]): DimensionName {
  if (dimensions.length === 0) return "overall";
  if (dimensions.includes("model")) return "model";
  if (dimensions.includes("overall")) return "overall";
  return dimensions[0];
}

/**
 * Resolve a member key to a display label:
 *   1. explicit dimension_catalog entry (authoritative);
 *   2. for the model dimension, the canonical catalog label (ChatGPT, ...);
 *   3. a humanized fallback that never shows a raw id.
 */
export function memberLabel(
  envelope: NormalizedEnvelope,
  dimension: DimensionName,
  memberKey: string,
): string {
  const fromCatalog = envelope.dimensionCatalog?.[dimension]?.[memberKey];
  if (fromCatalog) return fromCatalog;
  if (dimension === "model") {
    const label = getModelLabel(memberKey, "");
    if (label) return label;
  }
  return humanize(memberKey);
}

/** Whether a model member is a measurement-only lane kept out of analytics. */
export function isExcludedModelMember(dimension: DimensionName, memberKey: string): boolean {
  return dimension === "model" && isExcludedFromAnalytics(normalizeProviderKey(memberKey));
}

// ── Lenses ───────────────────────────────────────────────────────────────────

export interface Lens {
  key: string;
  label: string;
  group: LayerGroup;
  groupLabel: string;
  source: NormalizedLayer["source"];
  unit: Unit;
  dimensions: DimensionName[];
  layer: NormalizedLayer;
}

/**
 * Derive the ordered list of selectable lenses from an envelope. Sorted by group
 * order, then by the layer's original position so the headline AI Index always
 * leads. Layers with no plottable data never reach here (the validator drops
 * them), so every lens is guaranteed to render something.
 */
export function deriveLenses(envelope: NormalizedEnvelope): Lens[] {
  return envelope.layers
    .map((layer, index) => ({
      key: layer.key,
      label: layer.label,
      group: layer.group,
      groupLabel: groupLabel(layer.group),
      source: layer.source,
      unit: layer.unit,
      dimensions: layer.dimensions,
      layer,
      _index: index,
    }))
    .sort((a, b) => groupOrder(a.group) - groupOrder(b.group) || a._index - b._index)
    .map(({ _index, ...lens }) => lens);
}

// ── helpers ──────────────────────────────────────────────────────────────────

function humanize(key: string): string {
  if (!key) return "";
  const cleaned = key.replace(/^rv[_-]/i, "").replace(/[_-]+/g, " ").trim();
  if (!cleaned) return key;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}
